import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, Lock, Play, Trash2, Award, BookOpen, Filter, FileText } from 'lucide-react';
import TestCreatorView from './TestCreatorView';
import TestTakerView from './TestTakerView';
import { leitnerService } from '../../services/leitnerService';

const TestCenterView = ({ questions, bookId, batchUpdateProgress, onSubmitTest }) => {
  const [tests, setTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [view, setView] = useState('list');
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSubject, setFilterSubject] = useState('');
  const [filterGrade, setFilterGrade] = useState('');

  // Get unique categories from questions for filters
  const categories = useMemo(() => {
    const subjects = new Set();
    const grades = new Set();
    const topics = new Set();
    
    questions.forEach(q => {
      if (q.subject) subjects.add(q.subject);
      if (q.grade) grades.add(q.grade);
      if (q.topics) q.topics.forEach(t => topics.add(t));
    });
    
    return {
      subjects: Array.from(subjects).sort(),
      grades: Array.from(grades).sort(),
      topics: Array.from(topics).sort()
    };
  }, [questions]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const testsData = await leitnerService.getTests(bookId);
        setTests(testsData);

        const resultsData = await leitnerService.getTestResults(bookId);
        setTestResults(resultsData);
      } catch (error) {
        console.error('Failed to load test data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bookId]);

  // Check if a test is available based on schedule
  const isTestAvailable = (test) => {
    if (!test.schedule?.isScheduled) return true;
    
    const now = new Date();
    const startTime = new Date(test.schedule.startDateTime);
    const endTime = test.schedule.endDateTime ? new Date(test.schedule.endDateTime) : null;
    
    if (now < startTime) return false;
    if (endTime && now > endTime) return false;
    
    return true;
  };

  // Get test availability status
  const getTestStatus = (test) => {
    if (!test.schedule?.isScheduled) {
      return { status: 'available', message: 'Available now' };
    }
    
    const now = new Date();
    const startTime = new Date(test.schedule.startDateTime);
    const endTime = test.schedule.endDateTime ? new Date(test.schedule.endDateTime) : null;
    
    if (now < startTime) {
      return { 
        status: 'scheduled', 
        message: `Opens ${startTime.toLocaleDateString()} at ${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` 
      };
    }
    
    if (endTime && now > endTime) {
      return { status: 'closed', message: 'Test period ended' };
    }
    
    if (endTime) {
      return { 
        status: 'available', 
        message: `Available until ${endTime.toLocaleDateString()} ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` 
      };
    }
    
    return { status: 'available', message: 'Available now' };
  };

  // Filter tests by subject/grade
  const filteredTests = useMemo(() => {
    return tests.filter(test => {
      if (filterSubject && test.subject !== filterSubject) return false;
      if (filterGrade && test.grade !== filterGrade) return false;
      return true;
    });
  }, [tests, filterSubject, filterGrade]);

  const addTest = async (newTest) => {
    try {
      const testWithId = await leitnerService.addTest(bookId, newTest);
      setTests(prev => [...prev, testWithId]);
      setView('list');
    } catch (error) {
      console.error('Failed to add test:', error);
    }
  };
  
  const addTestResult = async (newResult) => {
    try {
      const resultWithId = await leitnerService.saveTestResult(bookId, newResult);
      setTestResults(prev => [...prev, resultWithId]);
    } catch (error) {
      console.error('Failed to save test result:', error);
    }
  };

  const deleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test? This will not delete past results.')) {
      try {
        await leitnerService.deleteTest(bookId, testId);
        setTests(prev => prev.filter(t => t.id !== testId));
      } catch (error) {
        console.error('Failed to delete test:', error);
      }
    }
  };

  const startTest = (test) => {
    if (!isTestAvailable(test)) return;
    setSelectedTest(test);
    setView('take');
  };
  
  const handleTestFinished = async (result, detailedResults) => {
    await addTestResult(result);
    
    // Call the onSubmitTest prop if provided
    if (onSubmitTest) {
      onSubmitTest(result, detailedResults);
    }
    
    const incorrectUpdates = Object.entries(detailedResults)
      .filter(([, answer]) => !answer.isCorrect)
      .map(([questionId, answer]) => ({
        questionId,
        newBox: 1,
        isSubQuestion: answer.isSubQuestion || false,
        parentQuestionId: answer.parentQuestionId || null
      }));

    if (incorrectUpdates.length > 0 && batchUpdateProgress) {
      try {
        await batchUpdateProgress(incorrectUpdates);
      } catch (error) {
        console.error('Failed to update progress:', error);
      }
    }
    
    setView('list');
  };

  // Create Test View
  if (view === 'create') {
    // Pass all questions - TestCreatorView will handle both simple and stem types
    return (
      <TestCreatorView 
        questions={questions}
        categories={categories}
        bookId={bookId}
        addTest={addTest} 
        onCancel={() => setView('list')} 
      />
    );
  }

  // Take Test View
  if (view === 'take' && selectedTest) {
    const testQuestions = selectedTest.questionIds
      .map(id => questions.find(q => q.id === id))
      .filter(q => !!q && q.type === 'simple');
    
    if (testQuestions.length === 0) {
      return (
        <div className="text-center bg-slate-800 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-red-400">Error</h2>
          <p className="mt-2 text-slate-300">
            This test contains no valid questions. It's possible the original questions were deleted.
          </p>
          <button 
            onClick={() => setView('list')} 
            className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md"
          >
            Back to List
          </button>
        </div>
      );
    }

    return (
      <TestTakerView 
        test={selectedTest} 
        questions={testQuestions} 
        onFinish={handleTestFinished}
        onSubmitTest={onSubmitTest}
        onBackToList={() => setView('list')}
      />
    );
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">Loading test center...</p>
      </div>
    );
  }

  // Test List View
  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-indigo-400">Available Tests</h2>
        <button 
          onClick={() => setView('create')} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition-transform transform hover:scale-105"
        >
          + Create New Test
        </button>
      </div>

      {/* Filters */}
      {(categories.subjects.length > 0 || categories.grades.length > 0) && (
        <div className="mb-6 p-3 bg-slate-700/50 rounded-lg flex flex-wrap gap-3 items-center">
          <Filter size={16} className="text-slate-400" />
          {categories.subjects.length > 0 && (
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
            >
              <option value="">All Subjects</option>
              {categories.subjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {categories.grades.length > 0 && (
            <select
              value={filterGrade}
              onChange={(e) => setFilterGrade(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
            >
              <option value="">All Grades</option>
              {categories.grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          )}
          {(filterSubject || filterGrade) && (
            <button
              onClick={() => { setFilterSubject(''); setFilterGrade(''); }}
              className="text-xs text-slate-400 hover:text-white"
            >
              Clear
            </button>
          )}
        </div>
      )}
      
      {filteredTests.length === 0 ? (
        <div className="text-center bg-slate-800 p-8 rounded-lg">
          <p className="text-slate-400">
            {tests.length === 0 ? 'No tests created yet.' : 'No tests match the selected filters.'}
          </p>
          {tests.length === 0 && (
            <button 
              onClick={() => setView('create')} 
              className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md"
            >
              Create Your First Test
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTests.map(test => {
            const testStatus = getTestStatus(test);
            const available = testStatus.status === 'available';
            const userResults = testResults.filter(r => r.testId === test.id);
            const lastResult = userResults[userResults.length - 1];
            
            return (
              <div 
                key={test.id} 
                className={`bg-slate-700 rounded-lg overflow-hidden border ${
                  available ? 'border-slate-600' : 'border-slate-700 opacity-75'
                }`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-slate-200 text-lg">{test.name}</h3>
                    <button
                      onClick={() => deleteTest(test.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  {/* Test Info */}
                  <div className="space-y-1 text-sm text-slate-400 mb-3">
                    <div className="flex items-center gap-2">
                      <BookOpen size={14} />
                      <span>{test.questionIds.length} questions</span>
                      {test.totalMarks && (
                        <span className="text-amber-400 flex items-center gap-1">
                          <Award size={12} />
                          {test.totalMarks} marks
                        </span>
                      )}
                    </div>
                    {test.timeLimitPerQuestion > 0 && (
                      <div className="flex items-center gap-2">
                        <Clock size={14} />
                        <span>{test.timeLimitPerQuestion}s per question</span>
                      </div>
                    )}
                    {test.instructions && (
                      <div className="flex items-center gap-2 text-indigo-400">
                        <FileText size={14} />
                        <span>Has instructions</span>
                      </div>
                    )}
                    {(test.subject || test.grade) && (
                      <div className="flex gap-2">
                        {test.subject && <span className="px-2 py-0.5 bg-slate-600 rounded text-xs">{test.subject}</span>}
                        {test.grade && <span className="px-2 py-0.5 bg-slate-600 rounded text-xs">{test.grade}</span>}
                      </div>
                    )}
                  </div>

                  {/* Schedule Status */}
                  <div className={`text-xs mb-3 flex items-center gap-1 ${
                    testStatus.status === 'available' ? 'text-green-400' :
                    testStatus.status === 'scheduled' ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {testStatus.status === 'scheduled' && <Calendar size={12} />}
                    {testStatus.status === 'closed' && <Lock size={12} />}
                    {testStatus.message}
                  </div>

                  {/* Last Result */}
                  {lastResult && (
                    <div className="text-xs text-slate-500 mb-3 p-2 bg-slate-800 rounded">
                      Last attempt: {lastResult.score}/{lastResult.totalQuestions} 
                      ({Math.round((lastResult.score / lastResult.totalQuestions) * 100)}%)
                    </div>
                  )}

                  {/* Start Button */}
                  <button
                    onClick={() => startTest(test)}
                    disabled={!available}
                    className={`w-full py-2 px-4 rounded-md font-semibold flex items-center justify-center gap-2 transition ${
                      available 
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                        : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {available ? (
                      <>
                        <Play size={16} />
                        Start Test
                      </>
                    ) : (
                      <>
                        <Lock size={16} />
                        {testStatus.status === 'scheduled' ? 'Not Yet Available' : 'Test Closed'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestCenterView;